import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

/**
 * Composant de footer unifié pour toutes les pages
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.pageType - Type de page ('home', 'about', 'services', 'products', 'projects', 'contact')
 */
const EnhancedFooter = ({ pageType = 'home' }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  // Liens du footer spécifiques à chaque page
  const pageLinks = {
    home: [
      { to: '/about', label: t('navigation.about') },
      { to: '/services', label: t('navigation.services') },
      { to: '/products', label: t('navigation.products') },
      { to: '/projects', label: t('navigation.projects') }
    ],
    about: [
      { to: '/', label: t('navigation.home') },
      { to: '/services', label: t('navigation.services') },
      { to: '/products', label: t('navigation.products') },
      { to: '/projects', label: t('navigation.projects') }
    ],
    services: [
      { to: '/', label: t('navigation.home') },
      { to: '/about', label: t('navigation.about') },
      { to: '/products', label: t('navigation.products') },
      { to: '/projects', label: t('navigation.projects') }
    ],
    products: [
      { to: '/', label: t('navigation.home') },
      { to: '/about', label: t('navigation.about') },
      { to: '/services', label: t('navigation.services') },
      { to: '/projects', label: t('navigation.projects') }
    ],
    projects: [
      { to: '/', label: t('navigation.home') },
      { to: '/about', label: t('navigation.about') },
      { to: '/services', label: t('navigation.services') },
      { to: '/products', label: t('navigation.products') }
    ],
    contact: [
      { to: '/', label: t('navigation.home') },
      { to: '/about', label: t('navigation.about') },
      { to: '/services', label: t('navigation.services') },
      { to: '/products', label: t('navigation.products') }
    ]
  };

  // Liens de services
  const serviceLinks = [
    { to: '/services', label: t('services.main.pneumatic.title') },
    { to: '/services', label: t('services.main.training.title') },
    { to: '/services', label: t('services.technical.maintenance') }
  ];

  // Informations de contact
  const contactInfo = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px', marginRight: '10px', marginTop: '3px', flexShrink: 0, color: 'var(--assco-orange)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      text: 'Cité 392 Logts LSP, Tala Ouariane, Bejaia, Algérie'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px', marginRight: '10px', marginTop: '3px', flexShrink: 0, color: 'var(--assco-orange)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      text: '+213 661462837 / 0658 65 83 64'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px', marginRight: '10px', marginTop: '3px', flexShrink: 0, color: 'var(--assco-orange)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      text: 'allaoua.felfoul@assco-dz.com / felfoul.aimed.eddine@gmail.com'
    }
  ];

  // Réseaux sociaux
  const socialLinks = [
    {
      name: 'Facebook',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
        </svg>
      ),
      url: '#'
    },
    {
      name: 'Twitter',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
        </svg>
      ),
      url: '#'
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
        </svg>
      ),
      url: '#'
    },
    {
      name: 'Instagram',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
      url: '#'
    }
  ];

  return (
    <footer className="modern-footer">
      <div className="container">
        <div className="footer-grid">
          {/* Logo and Company Info */}
          <div className="footer-column">
            <div className="footer-logo-container">
              <img 
                src="/images/assco-white.png" 
                alt="ASSCO Logo" 
                style={{ 
                  height: '40px',
                  marginBottom: '1rem'
                }} 
              />
            </div>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
              ALGERIA SENSING SOLUTIONS COMPANY
            </p>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('footer.rights')} © {currentYear}
            </p>
          </div>
          
          {/* Quick Links */}
          <div className="footer-column">
            <h3 className="footer-title">{t('navigation.home')}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {pageLinks[pageType].map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services */}
          <div className="footer-column">
            <h3 className="footer-title">{t('navigation.services')}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div className="footer-column">
            <h3 className="footer-title">{t('navigation.contact')}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {contactInfo.map((item, index) => (
                <li key={index} className="footer-contact-item" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {item.icon}
                  <span style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
            © {currentYear} ASSCO. {t('footer.rights')}.
          </p>
          
          <div className="footer-social">
            {socialLinks.map((social, index) => (
              <a 
                key={index} 
                href={social.url} 
                className="footer-social-link" 
                aria-label={social.name}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default EnhancedFooter;
